import { lazy as reactLazy, type ComponentType, type LazyExoticComponent } from 'react';

// One global timestamp guards against infinite reload loops when a chunk-load
// error is caused by a persistent network problem rather than a stale deploy.
// If the previous reload happened within the debounce window and we hit the
// same error again, we surface it to the route-level error boundary instead
// of reloading a second time.
const RELOAD_TIMESTAMP_KEY = '__aurous_chunk_reload_at__';
const RELOAD_DEBOUNCE_MS = 10_000;

// Browser variants of the dynamic-import-failed error. The exact wording
// differs across engines; the error code does not. Match on message text.
const CHUNK_ERROR_PATTERNS: RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk [\d]+ failed/i,
  /Loading CSS chunk [\d]+ failed/i,
];

export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const message =
    err instanceof Error
      ? `${err.name}: ${err.message}`
      : typeof err === 'string'
        ? err
        : String((err as { message?: unknown })?.message ?? '');
  return CHUNK_ERROR_PATTERNS.some((p) => p.test(message));
}

function readReloadTimestamp(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? 0) || 0;
  } catch {
    return 0;
  }
}

function writeReloadTimestamp(ts: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(ts));
  } catch {
    // sessionStorage can be unavailable (sandboxed iframes, locked-down
    // browsers). Without persistence we'll still reload once — we just lose
    // the reload-loop guard. The route ErrorBoundary will catch the second
    // failure.
  }
}

export interface LoadWithRetryOptions {
  now?: () => number;
  reload?: () => void;
  debounceMs?: number;
}

// Exported separately so it can be unit-tested without going through React.lazy.
export async function loadWithRetry<T>(
  factory: () => Promise<T>,
  options: LoadWithRetryOptions = {},
): Promise<T> {
  const now = options.now ?? Date.now;
  const reload = options.reload ?? (() => window.location.reload());
  const debounceMs = options.debounceMs ?? RELOAD_DEBOUNCE_MS;

  try {
    return await factory();
  } catch (err) {
    if (!isChunkLoadError(err)) throw err;

    const last = readReloadTimestamp();
    const t = now();
    if (t - last < debounceMs) {
      // We've already attempted recovery in this session. Surface the error
      // to the route boundary so the customer can refresh manually.
      throw err;
    }

    writeReloadTimestamp(t);
    reload();

    // Keep Suspense in its fallback state until the page actually navigates.
    // Returning a rejected/throwing path would race the reload and flash the
    // error boundary; never-resolving is the cleanest UX.
    return await new Promise<T>(() => {});
  }
}

/**
 * Drop-in replacement for React.lazy() that auto-recovers from stale-chunk
 * errors after a deploy. When a dynamic import 404s (because the hashed
 * chunk filename baked into the tab's running JS no longer exists on the
 * server), the helper triggers one full-page reload so the browser picks
 * up the freshly-deployed index.html and its new chunk hashes.
 *
 * Subsequent failures within the debounce window propagate so the route
 * ErrorBoundary can render a recover-manually surface.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return reactLazy(() => loadWithRetry(factory)) as LazyExoticComponent<T>;
}

/**
 * Returns true if it has been long enough since the last reload attempt to
 * try recovering again. Shared by the lazy-load layer and the route error
 * boundary so they coordinate on a single sessionStorage key — otherwise a
 * boundary-level safety-net reload could fire after the lazy layer just did
 * one, producing a visible double-reload.
 */
export function shouldAttemptChunkReload(now: number = Date.now()): boolean {
  const last = readReloadTimestamp();
  return now - last >= RELOAD_DEBOUNCE_MS;
}

export function markChunkReloadAttempted(now: number = Date.now()): void {
  writeReloadTimestamp(now);
}

// Internal — exposed for tests only.
export const __internal = {
  RELOAD_TIMESTAMP_KEY,
  RELOAD_DEBOUNCE_MS,
};
